import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Modal,
  Backdrop,
  Fade,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";

const integrations = [
  { name: "Shopify", platform: "Shopify" },
  { name: "Meta Ads", platform: "Meta Ads" },
  { name: "Google Ads", platform: "Google Ads" },
];

const schemas = {
  Shopify: {
    requiredFields: ["Order ID", "Customer Name", "Total Amount", "Order Date"],
    optionalFields: ["Shipping Address", "Product Details"],
  },
  "Meta Ads": {
    requiredFields: ["Ad ID", "Ad Name", "Impressions", "Clicks", "Spend", "Delivery Date"],
    optionalFields: ["Conversions", "CTR"],
  },
  "Google Ads": {
    requiredFields: ["Campaign ID", "Campaign Name", "Impressions", "Clicks", "Cost", "Delivery Date"],
    optionalFields: ["Conversions", "CTR"],
  },
};

const DataManagementPage = () => {
  const [integrationStatus, setIntegrationStatus] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [apiKey, setApiKey] = useState("");

  // Handle API Key Modal
  const handleOpenApiKeyModal = (platform) => {
    setSelectedPlatform(platform);
    setApiKeyModalOpen(true);
  };

  const handleCloseApiKeyModal = () => {
    setApiKeyModalOpen(false);
    setSelectedPlatform("");
    setApiKey("");
  };

const handleSaveApiKey = async () => {
  if (!apiKey) {
    alert("API Key cannot be empty.");
    return;
  }

  try {
    const response = await fetch(`/api/integrations/api-key/${selectedPlatform}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`, // Replace with your token retrieval logic
      },
      body: JSON.stringify({ api_key: apiKey }),
    });

    if (response.ok) {
      const data = await response.json();
      alert(`API Key saved successfully for ${selectedPlatform}!`);
      setIntegrationStatus((prevStatus) => ({
        ...prevStatus,
        [selectedPlatform]: true,
      }));
      handleCloseApiKeyModal();
    } else {
      const error = await response.json();
      alert(`Failed to save API Key: ${error.detail || "Unknown error"}`);
    }
  } catch (error) {
    console.error("Error saving API Key:", error);
    alert("An error occurred while saving the API Key. Please try again.");
  }
};


  const handleOpenModal = (platform) => {
    setSelectedPlatform(platform);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedPlatform("");
    setSelectedFile(null);
    setPreviewData([]);
    setErrors([]);
  };

  const handleFileSelection = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "text/csv") {
      setSelectedFile(file);
      parseCSV(file);
    } else {
      alert("Please upload a valid CSV file.");
    }
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const rows = text.split("\n").map((row) => row.split(","));
      validateCSV(rows, schemas[selectedPlatform]);
    };
    reader.readAsText(file);
  };

  const validateCSV = (data, schema) => {
    const { requiredFields, optionalFields } = schema;
    const headers = data[0];
    const validationErrors = [];

    requiredFields.forEach((field) => {
      if (!headers.includes(field)) {
        validationErrors.push(`Missing required field: ${field}`);
      }
    });

    headers.forEach((header) => {
      if (![...requiredFields, ...optionalFields].includes(header)) {
        validationErrors.push(`Unexpected field: ${header}`);
      }
    });

    setErrors(validationErrors);
    if (validationErrors.length === 0) {
      setPreviewData(data);
    }
  };

  const handleUpload = () => {
    setUploading(true);
    setTimeout(() => {
      setUploading(false);
      alert(`CSV uploaded successfully for ${selectedPlatform}!`);
      handleCloseModal();
    }, 2000);
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        Data Management
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Manage your integrations and upload data manually to keep everything in sync.
      </Typography>

      <Grid container spacing={4}>
        {integrations.map(({ name, platform }) => (
          <Grid item xs={12} sm={6} md={4} key={platform}>
            <Paper
              elevation={3}
              sx={{
                padding: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                height: "320px",
                borderRadius: 2,
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                {name}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: integrationStatus[platform] ? "green" : "gray", mb: 2 }}
              >
                {integrationStatus[platform] ? "Connected" : "Not Connected"}
              </Typography>

              <Button
                variant="contained"
                sx={{ width: "100%", mb: 2 }}
                onClick={() => handleOpenApiKeyModal(platform)}
              >
                Add API Key
              </Button>

              <Button
                variant="outlined"
                sx={{ width: "100%" }}
                onClick={() => handleOpenModal(platform)}
              >
                Upload CSV
              </Button>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* API Key Modal */}
      <Modal
        open={apiKeyModalOpen}
        onClose={handleCloseApiKeyModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={apiKeyModalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Add API Key for {selectedPlatform}
            </Typography>
            <TextField
              label="API Key"
              variant="outlined"
              fullWidth
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Button
              variant="contained"
              color="success"
              fullWidth
              onClick={handleSaveApiKey}
            >
              Save API Key
            </Button>
          </Box>
        </Fade>
      </Modal>

      {/* CSV Upload Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 600,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Upload CSV for {selectedPlatform}
            </Typography>

            {schemas[selectedPlatform] && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Required Fields: {schemas[selectedPlatform].requiredFields.join(", ")}
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Optional Fields: {schemas[selectedPlatform].optionalFields.join(", ")}
                </Typography>
              </>
            )}

            {selectedFile ? (
              <>
                {errors.length > 0 ? (
                  <>
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                      Validation Errors:
                    </Typography>
                    <ul>
                      {errors.map((error, index) => (
                        <li key={index}>
                          <Typography variant="body2" color="error">
                            {error}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <>
                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            {previewData[0]?.map((header, index) => (
                              <TableCell key={index}>{header}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewData.slice(1, 5).map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                              {row.map((cell, cellIndex) => (
                                <TableCell key={cellIndex}>{cell}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Button
                      variant="contained"
                      color="success"
                      sx={{ width: "100%" }}
                      onClick={handleUpload}
                      disabled={uploading}
                    >
                      {uploading ? <CircularProgress size={24} /> : "Upload Data"}
                    </Button>
                  </>
                )}
              </>
            ) : (
              <Button
                variant="outlined"
                component="label"
                sx={{ width: "100%" }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={handleFileSelection}
                />
              </Button>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default DataManagementPage;
